import { onMounted, ref } from 'vue'
import Highcharts from 'highcharts';
import 'highcharts/modules/accessibility';
import 'highcharts/modules/pattern-fill';
import handleHighchartData from './methods/handle-highchart-data'

import themeTotalkredit from './themes/highchart-theme.totalkredit'
import themeSparNord from './themes/highchart-theme.sparnord'
import themeNykredit from './themes/highchart-theme.nykredit'

import lineAndSplineConfig from './configs/line-and-spline.config'
import columnConfig from './configs/column.config';
import defaultConfig from './configs/default.config';
import areaConfig from './configs/area.config';
import barConfig from './configs/bar.config';
import pieConfig from './configs/pie.config';
import forecastConfig from './configs/forecast.config';

export default {
    name: "HighchartGraph",
    props: {
        theme: {
            type: String,
            required: true
        },
        useAccessibilityStyles: {
            type: Boolean,
            required: true,
            default: true
        },
        title: {
            type: String,
            required: false,
            default: ""
        },
        subtitle: {
            type: String,
            required: false,
            default: ""
        },
        yaxisTitle: {
            type: String,
            required: false,
            default: ""
        },
        xaxisTitle: {
            type: String,
            required: false,
            default: ""
        },
        yaxisLabelFormat: {
            type: String,
            required: false,
            default: ""
        },
        accessibilityDescriptionXaxis: {
            type: String,
            required: true
        },
        accessibilityRangeDescriptionXaxis: {
            type: String,
            required: false,
            default: ""
        },
        accessibilityDescriptionYaxis: {
            type: String,
            required: true
        },
        tooltipValueSuffix: {
            type: String,
            required: false,
            default: ""
        },
        graphData: {
            type: Array,
            required: true
        },
        graphType: {
            type: String,
            required: true,
            default: 'line'
        },
        colors: {
            type: Array,
            required: false
        },
        forecastSplitDate: {
            type: Number,
            required: false,
            default: ""
        },
        forecastSplitLabel: {
            type: String,
            required: false
        },
        yAxisDecimals: {
            type: Number,
            required: false,
            default: 0
        }
    },
    setup(props) {
        const graphConfig = ref({})
        const graphTheme = ref({})
        const highchartData = ref({})
        const accessibilityGraphStyles = [
            "ShortDashDot",
            "ShortDot",
            "Dash",
            "ShortDash"
        ]

        function generateUniqueId() {
            return 'id-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
        }
        const graphUniqueId = generateUniqueId()

        const getGraphConfig = () => {
            highchartData.value = handleHighchartData(
                props,
                Highcharts.theme.colors,
                props.useAccessibilityStyles ? accessibilityGraphStyles : []
            )

            switch (props.graphType.toLowerCase()) {
                case "line":
                case "spline":
                    graphConfig.value = lineAndSplineConfig(highchartData.value)
                    break
                case "forecast":
                    graphConfig.value = forecastConfig(highchartData.value)
                    break
                case "column":
                    graphConfig.value = columnConfig(highchartData.value)
                    break
                case "area":
                    graphConfig.value = areaConfig(highchartData.value)
                    break
                case "bar":
                    graphConfig.value = barConfig(highchartData.value)
                    break
                case "pie":
                    graphConfig.value = pieConfig(highchartData.value)
                    break
                default: // it defaults to a line graph if it doesn't match the provided graphType
                    graphConfig.value = defaultConfig(highchartData.value)
                    break
            }
        }

        const getGraphTheme = () => {
            switch (props.theme.toLowerCase()) {
                case "totalkredit":
                    graphTheme.value = themeTotalkredit
                    break
                case "nykredit":
                    graphTheme.value = themeNykredit
                    break
                case "sparnord":
                    graphTheme.value = themeSparNord
                    break
                default:
                    graphTheme.value = themeNykredit
                    break
            }
        }

        onMounted(() => {
            getGraphTheme()
            Highcharts.theme = graphTheme.value
            Highcharts.setOptions(Highcharts.theme)
            Highcharts.setOptions({ lang: { thousandsSep: '.', decimalPoint: ',' } })
            getGraphConfig()

            Highcharts.chart(graphUniqueId, graphConfig.value)
        })

        return {
            graphUniqueId
        }
    }
}